import { useMemo, useState } from 'react'
import { DataGridPro, ExtendedDataGridProProps } from '@cibo/ui'
import { Box, LinearProgress } from '@mui/material'
import { GridSortModel, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid-pro'

import { useOrganizations } from '../..'
import { OrganizationQuery, OrganizationSortField } from '@cibo/core'

const FDR_SORT_FIELDS = [
  'enrollingFieldCount',
  'enrollingAcres',
  'enrolledFieldCount',
  'enrolledAcres',
]

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

interface PaginatedOrgsTableProps extends Omit<ExtendedDataGridProProps, 'rows'> {
  programId?: string
}

export const PaginatedOrgsTable = ({
  autoHeight = true,
  columnVisibilityModel,
  components = {},
  componentsProps,
  pinnedColumns = {},
  onPageChange,
  onPageSizeChange,
  onRowClick,
  pageSize = 10,
  page = 0,
  programId,
  ...dataGridProProps
}: PaginatedOrgsTableProps) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [uncontrolledPage, setUncontrolledPage] = useState(page)
  const localPage = !!onPageChange ? page : uncontrolledPage
  const [uncontrolledPageSize, setUncontrolledPageSize] = useState(pageSize)
  const localPageSize = !!onPageSizeChange ? pageSize : uncontrolledPageSize

  const requestQuery = useMemo(() => {
    const firstSortModel = sortModel && sortModel[0]

    const basicSortQuery =
      !!firstSortModel && !FDR_SORT_FIELDS.includes(firstSortModel.field)
        ? {
            sortField: firstSortModel.field as OrganizationSortField,
            sortDirection: firstSortModel.sort as 'asc' | 'desc',
          }
        : undefined

    return {
      limit: localPageSize,
      offset: localPage * localPageSize,
      fdrStats: [
        {
          programId,
          status: 'enrolled',
          sort:
            firstSortModel?.field === 'enrolledFieldCount'
              ? {
                  sortDir: firstSortModel.sort,
                  value: ['fields'],
                }
              : firstSortModel?.field === 'enrolledAcres'
              ? {
                  sortDir: firstSortModel.sort,
                  value: ['acres'],
                }
              : undefined,
        },
        {
          programId,
          status: 'enrolling',
          sort:
            firstSortModel?.field === 'enrollingFieldCount'
              ? {
                  sortDir: firstSortModel.sort,
                  value: ['fields'],
                }
              : firstSortModel?.field === 'enrollingAcres'
              ? {
                  sortDir: firstSortModel.sort,
                  value: ['acres'],
                }
              : undefined,
        },
      ],
      ...basicSortQuery,
    }
  }, [sortModel, localPage, localPageSize])

  const organizations = useOrganizations(requestQuery as OrganizationQuery)
  const rows = organizations.data ? organizations.data.items : []

  const handlePageChange = (newPage: number, details: any) =>
    !!onPageChange ? onPageChange(newPage, details) : setUncontrolledPage(newPage)

  const handlePageSizeChange = (newPageSize: number, details: any) =>
    !!onPageSizeChange
      ? onPageSizeChange(newPageSize, details)
      : setUncontrolledPageSize(newPageSize)

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel)
  }

  return (
    <Box data-testid="organizations-table">
      <DataGridPro
        autoHeight={autoHeight}
        components={{
          LoadingOverlay: () => <LinearProgress sx={{ marginBottom: -0.5 }} />,
          ...components,
        }}
        componentsProps={componentsProps}
        density="compact"
        disableColumnFilter={true}
        getRowId={row => row.orgInfo.orgId}
        initialState={{
          columns: {
            columnVisibilityModel,
          },
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
            ...pinnedColumns,
          },
          sorting: {
            // sortModel,
          },
        }}
        loading={organizations.isFetching}
        pageSize={localPageSize}
        onRowClick={onRowClick}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        page={localPage}
        paginationMode="server"
        sortingMode="server"
        rowCount={organizations.data?.numAvailable || 0}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        rows={rows}
        pagination
        keepNonExistentRowsSelected
        {...dataGridProProps}
      />
    </Box>
  )
}
